import { createSelector } from '@reduxjs/toolkit';

import { Lumber, User } from '@paragon/contract-library';
import { getLoggedInUser } from '@paragon/login-tools';

import { useMaterialListsSelector } from '../lib/components/hooks';
import { getNominalDimensionString, HasNominalDimensions, hasNominalDimensions } from './lumberSelectionUtility';

interface NicknameableLumberLikeObject extends HasNominalDimensions {
  species: string;
}

export function isLumberSuitableForNicknaming(lumber: object): lumber is NicknameableLumberLikeObject {
  return hasNominalDimensions(lumber) && (lumber as any).species;
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////// Hello friend! If you're trying to define more lumber nicknames, this is the method you're looking for. ////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const getLumberNickname = (currentUser: User | null, lumber: NicknameableLumberLikeObject): string | null => {
  // Note that the only properties available when defining nicknames are the current user and the lumber's species,
  // nominalThickness, and nominalWidth.  If you want to use any other properties, talk to a developer.

  // based on https://trello.com/c/6KQEjtjc/203-display-nickname-for-lumber-if-one-exists-and-allow-users-to-choose-lumber-by-nickname
  if (currentUser?.organization === 'G2 National' && lumber.species === 'RigidLam_LVL') {
    if (lumber.nominalThickness === '3.5') {
      switch (lumber.nominalWidth) {
        case '2.125':
          return '43V';

        case '3.5':
          return '44V';

        case '4.375':
          return '45V';
      }
    }

    if (lumber.nominalThickness === '5.25') {
      switch (lumber.nominalWidth) {
        case '2.125':
          return '63V';

        case '3.5':
          return '64V';

        case '4.275':
          return '65V';
      }
    }
  }

  return null;
};

const getActualDimensionString = (lumber: Lumber) => `${lumber.actualThickness}" x ${lumber.actualWidth}"`;

export type NominalLumberDimensionFormatter = (lumber: NicknameableLumberLikeObject) => string;
export const createNominalLumberDimensionFormatter = createSelector(
  getLoggedInUser,
  (currentUser): NominalLumberDimensionFormatter =>
    function formatNominalLumberDimensions(lumber: NicknameableLumberLikeObject) {
      const nickname = getLumberNickname(currentUser, lumber);
      const dimensionString = getNominalDimensionString(lumber);

      return nickname ? `${nickname}: ${dimensionString}` : dimensionString;
    },
);

export const useNominalLumberDimensionFormatter = (): NominalLumberDimensionFormatter =>
  useMaterialListsSelector((state) => createNominalLumberDimensionFormatter(state));

export const createActualLumberDimensionFormatter = createSelector(
  getLoggedInUser,
  (currentUser) =>
    function formatActualLumberDimensions(lumber: Lumber) {
      const nickname = getLumberNickname(currentUser, lumber);
      const dimensionString = getActualDimensionString(lumber);

      return nickname ? `${nickname}: ${dimensionString}` : dimensionString;
    },
);
